import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

export interface Toast {
  id: number;
  message: string;
  type: 'success' | 'warning' | 'error' | 'info';
  minimal: boolean;
}

type ToastAction =
  | { type: 'ADD'; toast: Toast }
  | { type: 'REMOVE'; id: number };

function toastReducer(state: Toast[], action: ToastAction): Toast[] {
  switch (action.type) {
    case 'ADD':
      return [...state, action.toast];
    case 'REMOVE':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (message: string, type: Toast['type'], minimal?: boolean) => void;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
});

let _nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const timersRef = useRef<Map<number, number>>(new Map());

  const removeToast = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    dispatch({ type: 'REMOVE', id });
  }, []);

  const addToast = useCallback(
    (message: string, type: Toast['type'], minimal = false) => {
      const id = _nextId++;
      const delay = minimal ? 3000 : 10000;
      dispatch({ type: 'ADD', toast: { id, message, type, minimal } });
      const timer = window.setTimeout(() => removeToast(id), delay);
      timersRef.current.set(id, timer);
    },
    [removeToast]
  );

  // Clean up all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

// Imperative API for non-React code (mirrors App.showPopupNotification)
let _imperativeAddToast: ToastContextValue['addToast'] | null = null;
let _pendingToasts: { message: string; type: Toast['type']; minimal: boolean }[] = [];

export function registerImperativeToast(addToast: ToastContextValue['addToast']) {
  _imperativeAddToast = addToast;
  // Flush any toasts that arrived before the provider mounted
  for (const t of _pendingToasts) {
    addToast(t.message, t.type, t.minimal);
  }
  _pendingToasts = [];
}

export function showToast(message: string, type: Toast['type'], minimal = false) {
  if (_imperativeAddToast) {
    _imperativeAddToast(message, type, minimal);
  } else {
    _pendingToasts.push({ message, type, minimal });
  }
}
